import { match, P } from 'ts-pattern';
import Icon from '~/components/Icon';
import Link from '~/components/Link';
import LinkButton from '~/components/button/LinkButton';
import { useClaimOverviewData } from './claimOverview';

// TODO: HR's contract wants `?address={zipcode}&has_qr_code=true&retailer={retailerId}` on this
// link. The zipcode is on shipment.fromAddress, but retailerId lives in config_happy_returns and
// isn't exposed to the customer UI yet — needs plumbing through settings.
const LOCATIONS_URL = 'https://locations.happyreturns.com';
const LOGO_URL = 'https://cdn.corso.com/img/hr-text-logo.png';
const APPLE_BADGE_URL = '/apple-add-to-wallet.svg';
const GOOGLE_BADGE_URL = '/google-add-to-wallet.svg';

const dropoffInstructions = [
  'Remove shipping materials',
  'Item barcodes will be scanned',
];

type HappyShipment = Extract<
  ReturnType<typeof useClaimOverviewData>['returnShipments'][number],
  { returnShipmentType: 'Consolidated_Dropoff' }
>;

function AddToWalletButton({
  appleWalletPassUrl,
  googleWalletPassUrl,
}: Pick<HappyShipment, 'appleWalletPassUrl' | 'googleWalletPassUrl'>) {
  const { userAgent } = navigator;

  return match({
    isAndroidDevice: /Android/i.test(userAgent),
    isAppleDevice: /iPhone|iPad|Macintosh/i.test(userAgent),
    appleWalletPassUrl,
    googleWalletPassUrl,
  })
    .with(
      { isAndroidDevice: true, googleWalletPassUrl: P.string },
      ({ googleWalletPassUrl: passUrl }) => (
        <Link to={passUrl} reloadDocument target="_blank" rel="noreferrer">
          <img
            alt="Add to Google Wallet"
            className="h-14"
            src={GOOGLE_BADGE_URL}
          />
        </Link>
      ),
    )
    .with(
      { isAppleDevice: true, appleWalletPassUrl: P.string },
      ({ appleWalletPassUrl: passUrl }) => (
        <Link to={passUrl} reloadDocument target="_top">
          <img
            alt="Add to Apple Wallet"
            className="h-16"
            src={APPLE_BADGE_URL}
          />
        </Link>
      ),
    )
    .otherwise(() => null);
}

export function HappyReturnQrCode({ shipment }: { shipment: HappyShipment }) {
  const { storeUrl, isEmbedded, isCancelled } = useClaimOverviewData();
  const {
    consolidatedDropoffCode,
    returnShipmentPdfAssetUrl,
    appleWalletPassUrl,
    googleWalletPassUrl,
    isExpired,
  } = shipment;

  if (isExpired || isCancelled) return null;
  return (
    <div className="flex w-full flex-col gap-4">
      <p className="subheading-sm text-on-surface">Dropoff Instructions</p>

      <ul className="flex list-disc flex-col gap-1 pl-5 body-md text-on-surface-variant marker:text-on-surface-variant">
        {dropoffInstructions.map((instruction) => (
          <li key={instruction}>{instruction}</li>
        ))}
      </ul>

      <figure className="flex w-full flex-col self-center overflow-hidden rounded-lg border border-outline">
        <figcaption className="flex items-center justify-center bg-[#FFD400] py-3">
          <img
            aria-hidden
            alt="Happy Returns"
            className="mt-1 h-8"
            src={LOGO_URL}
          />
        </figcaption>
        <div className="flex flex-col items-center gap-6 bg-surface p-6">
          <img
            alt={`Happy Returns QR code ${consolidatedDropoffCode}`}
            className="w-48"
            src={returnShipmentPdfAssetUrl}
          />
          <span className="label-lg tracking-widest text-on-surface-variant">
            {consolidatedDropoffCode}
          </span>

          <AddToWalletButton
            appleWalletPassUrl={appleWalletPassUrl}
            googleWalletPassUrl={googleWalletPassUrl}
          />
        </div>
      </figure>

      <div className="flex flex-col gap-4 md:flex-row">
        <LinkButton
          className="w-full"
          variant="filled"
          to={LOCATIONS_URL}
          target="_blank"
          rel="noreferrer"
        >
          Find a Happy Returns&reg; location
          <Icon icon="ArrowUpRightFromSquare" className="ml-2 size-4" />
        </LinkButton>

        {storeUrl && !isEmbedded && (
          <LinkButton
            className="w-full"
            to={storeUrl}
            variant="outlined"
            target="_blank"
            rel="noreferrer"
          >
            Back To Our Website
          </LinkButton>
        )}
      </div>
    </div>
  );
}
